import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import type {
  GetStoreListQuery,
  SetStoreHiddenMutation,
  SetStoreHiddenMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const SET_STORE_HIDDEN = gql(`
  mutation SetStoreHidden($storeId: Int!, $isHidden: Boolean!) {
    setStoreHidden(storeId: $storeId, isHidden: $isHidden) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

export default function AdminStoreHide() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [setStoreHidden] = useMutation<
    SetStoreHiddenMutation,
    SetStoreHiddenMutationVariables
  >(SET_STORE_HIDDEN, {
    onCompleted: (res) => {
      if (res.setStoreHidden.ok) {
        notify(
          selectedStore?.isHidden
            ? "매장이 다시 노출됩니다."
            : "매장이 소비자 앱에서 미노출 처리되었습니다."
        );
        setSelectedStore(null);
        refetch();
      } else {
        notifyError(
          res.setStoreHidden.error ?? "미노출 처리 중 오류가 발생했습니다."
        );
      }
    },
    onError: () => {
      notifyError("미노출 처리 중 오류가 발생했습니다.");
    },
  });

  const fullList = (data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [])
    .filter((store) => !store.isDeleted);
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false)
      )
    : fullList;

  const handleToggleHidden = (nextHidden: boolean) => {
    if (!selectedStore) return;
    const isConfirmed = window.confirm(
      nextHidden
        ? `"${selectedStore.title}" 매장을 소비자 앱에서 미노출 처리할까요? 주문·정산 데이터는 유지되며 언제든 다시 노출할 수 있습니다.`
        : `"${selectedStore.title}" 매장을 소비자 앱에 다시 노출할까요?`
    );
    if (isConfirmed) {
      setIsSaving(true);
      setStoreHidden({
        variables: { storeId: selectedStore.id, isHidden: nextHidden },
      }).finally(() => setIsSaving(false));
    }
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 미노출 처리"
      description="소비자 앱에서만 숨깁니다. 주문·정산 데이터는 그대로 남습니다."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            extraHeaders={
              <>
                <th className="px-4 py-2 font-medium text-gray-700">
                  가입상태
                </th>
                <th className="px-4 py-2 font-medium text-gray-700">
                  노출
                </th>
              </>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                        full.signupState === "승인" &&
                          "bg-green-100 text-green-800",
                        full.signupState === "요청" &&
                          "bg-yellow-100 text-yellow-800",
                        full.signupState === "거절" &&
                          "bg-red-100 text-red-800",
                        full.signupState === "요청완료" &&
                          "bg-orange-100 text-orange-800",
                        !["승인", "요청", "거절", "요청완료"].includes(
                          full.signupState ?? ""
                        ) && "bg-gray-100 text-gray-800"
                      )}
                    >
                      {full.signupState ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                        full.isHidden
                          ? "bg-gray-800 text-white"
                          : "bg-green-100 text-green-800"
                      )}
                    >
                      {full.isHidden ? "미노출" : "노출"}
                    </span>
                  </td>
                </>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <div className="mt-2 flex gap-1">
                  <span
                    className={clsx(
                      "px-2 py-0.5 inline-flex font-semibold rounded-full text-xs",
                      full.signupState === "승인" &&
                        "bg-green-100 text-green-800",
                      full.signupState === "요청" &&
                        "bg-yellow-100 text-yellow-800",
                      full.signupState === "거절" &&
                        "bg-red-100 text-red-800",
                      full.signupState === "요청완료" &&
                        "bg-orange-100 text-orange-800",
                      !["승인", "요청", "거절", "요청완료"].includes(
                        full.signupState ?? ""
                      ) && "bg-gray-100 text-gray-800"
                    )}
                  >
                    {full.signupState ?? "—"}
                  </span>
                  <span
                    className={clsx(
                      "px-2 py-0.5 inline-flex font-semibold rounded-full text-xs",
                      full.isHidden
                        ? "bg-gray-800 text-white"
                        : "bg-green-100 text-green-800"
                    )}
                  >
                    {full.isHidden ? "미노출" : "노출"}
                  </span>
                </div>
              );
            }}
            emptyText={
              keyword ? "검색 결과가 없습니다." : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "미노출 처리",
        emptyText: "매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="flex items-center gap-3">
              {selectedStore.img ? (
                <ResponsiveCDNImage
                  src={selectedStore.img}
                  alt={selectedStore.title}
                  className="w-20 h-20 rounded object-cover"
                  sizeRatio={0.2}
                />
              ) : (
                <div className="w-20 h-20 bg-gray-200 rounded" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {selectedStore.title}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedStore.address ?? "—"}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700 text-sm">
              <div>
                <span className="font-medium text-gray-800">연락처:</span>{" "}
                {selectedStore.seller?.contactNumber ?? "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">가입일:</span>{" "}
                {new Date(selectedStore.createdAt).toLocaleDateString()}
              </div>
              <div>
                <span className="font-medium text-gray-800">현재 상태:</span>{" "}
                {selectedStore.isHidden ? "미노출" : "노출"}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              미노출은 소비자 화면에서만 숨깁니다. 주문·정산 데이터는 유지되며
              삭제가 아닙니다.
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSelectedStore(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200"
              >
                선택 해제
              </button>
              {selectedStore.isHidden ? (
                <button
                  type="button"
                  onClick={() => handleToggleHidden(false)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <LoadingSpinnerInline />
                      처리 중...
                    </>
                  ) : (
                    "다시 노출"
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleToggleHidden(true)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white font-medium rounded-md hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <LoadingSpinnerInline />
                      처리 중...
                    </>
                  ) : (
                    "미노출 처리"
                  )}
                </button>
              )}
            </div>
          </div>
        ) : null,
      }}
    />
  );
}
